/**
 * Planning Engine Defaults Settings
 *
 * Workspace admins set defaults that pre-fill new scenarios in the Planning Engine,
 * plus Item Effort hours for altitude / Work Size bands used by viability planning.
 * Data is persisted in public.planning_workspace_defaults (migration 546+).
 */

import { useState, useEffect } from 'react';
import { Settings2, RotateCcw, Check } from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { usePlanningDefaultsStore, type PlanningDefaults, type PlanningModeDefault } from '../../../stores/prioritization/planningDefaultsStore';
import { useResourceTypesStore } from '../../../stores/prioritization/resourceTypesStore';
import { useTenantStore } from '../../../stores/prioritization/tenantStore';
import {
  ALTITUDE_EFFORT_BANDS,
  HOURS_PER_CAPACITY_UNIT,
  WORK_SIZE_HOURS,
  type WorkSizeBandKey,
} from '../../../lib/itemToPlanningAdapter';

const PLANNING_MODE_OPTIONS: { value: PlanningModeDefault; label: string; description: string }[] = [
  { value: 'priority_estimate', label: 'Priority Estimate', description: 'Capacity is estimated at the priority (item) level — no individual work units required.' },
  { value: 'work_units',        label: 'Work Units',        description: 'Capacity is derived from individual work units attached to each priority.' },
  { value: 'hybrid_remaining',  label: 'Hybrid Remaining',  description: 'Uses work units where defined; falls back to priority-level estimates for the remainder.' },
];

function FieldRow({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-6 px-5 py-4 border-b border-[var(--rule)] last:border-0">
      <div className="w-56 flex-shrink-0">
        <p className="text-sm font-medium text-[var(--text-pri)]">{label}</p>
        {hint && <p className="text-xs text-[var(--text-ter)] mt-0.5">{hint}</p>}
      </div>
      <div className="flex-1">{children}</div>
    </div>
  );
}

function hoursToCu(hours: number): number {
  return Math.ceil(Math.max(0, hours) / HOURS_PER_CAPACITY_UNIT);
}

export default function PlanningDefaultsSettings() {
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId ?? '');
  const { defaults, load, setDefaults, resetDefaults, loading, error } = usePlanningDefaultsStore();
  const { getEnabledTypes } = useResourceTypesStore();
  const enabledTypes = getEnabledTypes();

  const [saved, setSaved] = useState(false);
  const [local, setLocal] = useState<PlanningDefaults>(defaults);

  useEffect(() => {
    if (workspaceId) void load(workspaceId);
  }, [workspaceId, load]);

  // Sync local form state when DB data arrives
  useEffect(() => {
    setLocal(defaults);
  }, [defaults]);

  const handleChange = <K extends keyof PlanningDefaults>(key: K, value: PlanningDefaults[K]) => {
    setLocal((prev) => ({ ...prev, [key]: value }));
    setSaved(false);
  };

  const handleBandHoursChange = (band: WorkSizeBandKey, hours: number) => {
    setLocal((prev) => ({
      ...prev,
      itemEffortBandHours: {
        ...WORK_SIZE_HOURS,
        ...prev.itemEffortBandHours,
        [band]: hours,
      },
    }));
    setSaved(false);
  };

  const handleSave = async () => {
    await setDefaults(workspaceId, local);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleReset = async () => {
    await resetDefaults(workspaceId);
    setLocal(usePlanningDefaultsStore.getState().defaults);
    setSaved(false);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader icon={<Settings2 className="h-4 w-4 text-[var(--text-ter)]" />} iconBg="bg-[var(--slate-dim)]" title="Planning Engine Defaults" subtitle="Loading…" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <PageHeader
          icon={<Settings2 className="h-4 w-4 text-[var(--text-ter)]" />}
          iconBg="bg-[var(--slate-dim)]"
          title="Planning Engine Defaults"
          subtitle="Workspace defaults that pre-fill new planning scenarios and Item Effort estimates for viability planning. Individual scenarios can always be adjusted after creation."
        />
      </div>

      {error && (
        <div className="border border-[var(--coral)] dark:border-[var(--coral)] bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20 px-4 py-3">
          <p className="text-sm text-[var(--coral)] dark:text-[var(--coral)]">{error}</p>
        </div>
      )}

      <div className="border border-[var(--mustard)] dark:border-[var(--mustard)] bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/20 px-4 py-3">
        <p className="text-sm text-[var(--mustard)] dark:text-[var(--mustard)] font-medium mb-1">These are starting-point values</p>
        <p className="text-xs text-[var(--mustard)] dark:text-[var(--mustard)]">
          Scenario defaults apply when creating a new scenario. Item Effort band changes apply to new altitude / Work Size seeds and to items without a stored Effort value — they do not rewrite items with manual or planner Effort.
        </p>
      </div>

      <section className="space-y-3">
        <div>
          <h2 className="text-sm font-semibold text-[var(--text-sec)] uppercase tracking-wide">Scenario Defaults</h2>
          <p className="text-xs text-[var(--text-ter)] mt-1">Pre-fill values for the "Create Scenario" form.</p>
        </div>
        <div className="border border-[var(--rule)] bg-[var(--bg-raised)] divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
          <FieldRow label="Planning Mode" hint="How the engine estimates item capacity">
            <div className="space-y-2">
              {PLANNING_MODE_OPTIONS.map((opt) => (
                <label key={opt.value} className="flex items-start gap-3 cursor-pointer group">
                  <input type="radio" name="planningMode" value={opt.value} checked={local.planningMode === opt.value} onChange={() => handleChange('planningMode', opt.value)} className="mt-0.5 accent-indigo-600" />
                  <div>
                    <span className="text-sm font-medium text-[var(--text-pri)] group-hover:text-[var(--text-ter)] dark:group-hover:text-[var(--text-ter)] transition-colors">{opt.label}</span>
                    <p className="text-xs text-[var(--text-ter)]">{opt.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </FieldRow>

          <FieldRow label="Planning Weeks" hint="Duration of the planning horizon">
            <div className="flex items-center gap-3">
              <input type="number" min={1} max={104} value={local.planningWeeks}
                onChange={(e) => handleChange('planningWeeks', Math.max(1, parseInt(e.target.value) || 1))}
                className="w-24 text-sm px-3 py-1.5 rounded border border-[var(--rule)] bg-[var(--bg-raised)] text-[var(--text-pri)] focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              <span className="text-sm text-[var(--text-ter)]">weeks</span>
            </div>
          </FieldRow>

          <FieldRow label="Capacity Buffer" hint="Percentage reserved for unplanned work">
            <div className="flex items-center gap-3">
              <input type="number" min={0} max={80} step={5} value={local.bufferPct}
                onChange={(e) => handleChange('bufferPct', Math.max(0, Math.min(80, parseInt(e.target.value) || 0)))}
                className="w-24 text-sm px-3 py-1.5 rounded border border-[var(--rule)] bg-[var(--bg-raised)] text-[var(--text-pri)] focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              <span className="text-sm text-[var(--text-ter)]">%</span>
            </div>
          </FieldRow>

          <FieldRow label="Default WU Hours" hint="Fallback hours per work unit when not specified">
            <div className="flex items-center gap-3">
              <input type="number" min={1} max={160} value={local.defaultWuHours}
                onChange={(e) => handleChange('defaultWuHours', Math.max(1, parseInt(e.target.value) || 1))}
                className="w-24 text-sm px-3 py-1.5 rounded border border-[var(--rule)] bg-[var(--bg-raised)] text-[var(--text-pri)] focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              <span className="text-sm text-[var(--text-ter)]">hours</span>
            </div>
          </FieldRow>
        </div>
      </section>

      <section className="space-y-3">
        <div>
          <h2 className="text-sm font-semibold text-[var(--text-sec)] uppercase tracking-wide">Capacity Model Defaults</h2>
          <p className="text-xs text-[var(--text-ter)] mt-1">Pre-fill values for new capacity rows in the Planning Engine.</p>
        </div>
        <div className="border border-[var(--rule)] bg-[var(--bg-raised)] divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
          <FieldRow label="Default Resource Type" hint="Pre-selected type when adding a capacity row">
            <select value={local.defaultResourceType} onChange={(e) => handleChange('defaultResourceType', e.target.value)}
              className="text-sm px-3 py-1.5 rounded border border-[var(--rule)] bg-[var(--bg-raised)] text-[var(--text-pri)] focus:outline-none focus:ring-2 focus:ring-indigo-500">
              {enabledTypes.map((rt) => <option key={rt.key} value={rt.key}>{rt.label}</option>)}
            </select>
          </FieldRow>

          <FieldRow label="Weekly Capacity Hours" hint="Default hours per week per capacity row">
            <div className="flex items-center gap-3">
              <input type="number" min={1} max={168} value={local.weeklyCapacityHours}
                onChange={(e) => handleChange('weeklyCapacityHours', Math.max(1, parseInt(e.target.value) || 1))}
                className="w-24 text-sm px-3 py-1.5 rounded border border-[var(--rule)] bg-[var(--bg-raised)] text-[var(--text-pri)] focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              <span className="text-sm text-[var(--text-ter)]">hrs / week</span>
            </div>
          </FieldRow>
        </div>
      </section>

      <section className="space-y-3">
        <div>
          <h2 className="text-sm font-semibold text-[var(--text-sec)] uppercase tracking-wide">Item Effort by Altitude</h2>
          <p className="text-xs text-[var(--text-ter)] mt-1">
            Hours midpoints for altitude levels 1–5. Viability planning converts hours to capacity units (1 CU ≈ {HOURS_PER_CAPACITY_UNIT}h).
          </p>
        </div>
        <div className="border border-[var(--rule)] bg-[var(--bg-raised)] overflow-hidden">
          <div className="grid grid-cols-[1fr_7rem_5rem] gap-3 px-5 py-2.5 border-b border-[var(--rule)] bg-[var(--surface)] dark:bg-gray-950/40 text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide">
            <span>Altitude</span>
            <span>Hours</span>
            <span>CU</span>
          </div>
          {ALTITUDE_EFFORT_BANDS.map(({ altitude, band }) => {
            const hours = local.itemEffortBandHours[band] ?? WORK_SIZE_HOURS[band];
            return (
              <div
                key={band}
                className="grid grid-cols-[1fr_7rem_5rem] gap-3 items-center px-5 py-3 border-b border-[var(--rule)] last:border-0"
              >
                <p className="text-sm font-medium text-[var(--text-pri)]">
                  Altitude {altitude}
                </p>
                <input
                  type="number"
                  min={1}
                  max={100000}
                  value={hours}
                  onChange={(e) =>
                    handleBandHoursChange(band, Math.max(1, parseInt(e.target.value) || 1))
                  }
                  className="w-full text-sm px-3 py-1.5 rounded border border-[var(--rule)] bg-[var(--bg-raised)] text-[var(--text-pri)] focus:outline-none focus:ring-2 focus:ring-indigo-500 tabular-nums"
                />
                <span className="text-sm text-[var(--text-sec)] tabular-nums">
                  {hoursToCu(hours)} CU
                </span>
              </div>
            );
          })}
        </div>
      </section>

      <div className="flex items-center gap-3 pt-2">
        <button onClick={() => void handleSave()}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-[var(--slate)] hover:bg-[var(--slate)] transition-colors">
          {saved ? <Check className="h-4 w-4" /> : null}
          {saved ? 'Saved' : 'Save defaults'}
        </button>
        <button onClick={() => void handleReset()}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] transition-colors">
          <RotateCcw className="h-4 w-4" /> Reset to defaults
        </button>
      </div>
    </div>
  );
}
